import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import BaseCard from "../../components/ui/BaseCard";
import BaseDataTableV2 from "../../components/ui/BaseDataTableV2";
import BaseTextField from "../../components/ui/BaseTextField";
import BaseButton from "../../components/ui/BaseButton";
import BaseDropdownV2 from "../../components/ui/BaseDropdownV2";

import { fetchAllSubQualificationTypes } from "../../services/api/qualification";
import { debounce } from "../../utils/helpers";

import AddEditSubQualificationModal from "./modals/AddEditSubQualificationModal";

const menuOptions = [];

export default function SubQualificationsTable() {
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [rowCountState, setRowCountState] = useState(0);

  const [isSubQualificationModalOpen, setSubQualificationModalOpen] =
    useState(false);
  const [subQualificationMode, setSubQualificationMode] = useState("add");
  const [SubQualificationInitial, setSubQualificationInitial] = useState(null);

  const params = useMemo(
    () => ({
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      search: searchQuery || undefined,
    }),
    [paginationModel, searchQuery]
  );

  const {
    data: subQualifications,
    isPending,
    refetch,
  } = useQuery({
    queryKey: ["subQualifications", params],
    queryFn: () => fetchAllSubQualificationTypes(params),
    keepPreviousData: true,
  });

  useEffect(() => {
    setRowCountState((prev) => subQualifications?.data?.total ?? prev);
  }, [subQualifications?.data?.total]);

  const rows = useMemo(() => {
    const list = subQualifications?.data ?? [];
    return list?.data?.map((sq) => ({
      id: sq.id,
      name: sq.name,
      qualificationType: sq.qualificationType,
      qualificationTypeId:
        sq.qualificationTypeId ?? sq.qualificationType?.id ?? null,
    }));
  }, [subQualifications]);

  const debouncedSetSearchQuery = useRef(
    debounce((val) => {
      setSearchQuery(val);
      setPaginationModel((m) => ({ ...m, page: 0 }));
    }, 400)
  );

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    debouncedSetSearchQuery.current(val);
  };

  const openAdd = () => {
    setSubQualificationMode("add");
    setSubQualificationInitial(null);
    setSubQualificationModalOpen(true);
  };

  const openEdit = (row) => {
    setSubQualificationMode("edit");
    setSubQualificationInitial({
      id: row.id,
      name: row.name,
      qualificationTypeId: row.qualificationTypeId,
    });
    setSubQualificationModalOpen(true);
  };

  const handleMenuClick = (item, row) => {
    if (item.value === "EDIT") openEdit(row);
    if (item.value === "DELETE") {
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Sub-Qualification",
      flex: 1,
      renderCell: (p) => <button className="text-primary">{p.row.name}</button>,
    },
    {
      field: "qualification",
      headerName: "Qualification Type",
      flex: 1,
      renderCell: (p) => <span>{p?.row?.qualificationType?.name ?? "-"}</span>,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.6,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <BaseDropdownV2
          menuOptions={menuOptions}
          triggerElement={
            <i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base" />
          }
          onItemClick={(item) => handleMenuClick(item, params.row)}
        />
      ),
    },
  ];

  return (
    <>
      <div className="bg-white rounded-lg px-5 py-3.5 shadow-md mt-4 sticky top-10 z-10">
        <div className="flex justify-between items-center w-full gap-4">
          <div className="flex items-center border-r-[1px] border-[#8C8C8C] pr-5">
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary"><i className="fa-light fa-filter text-white text-sm"></i></span>
            <p className="text-primary ml-2">Add Filter</p>
          </div>

          <div className="w-[76%]">
            <BaseTextField
              placeholder="Search sub-qualifications"
              value={searchInput}
              onChange={handleSearch}
              startAdornment={
                <i className="fa-light fa-magnifying-glass text-lg" />
              }
            />
          </div>

          <BaseButton
            className="md:w-auto w-full"
            variant="contained"
            color="primary"
            style={{ padding: "6px 12px" }}
            onClick={openAdd}
          >
            Create New Sub-Qualification
          </BaseButton>
        </div>
      </div>

      <BaseCard
        className="rounded-tl-xl rounded-tr-xl shadow-lg mt-5"
        elevation={0}
      >
            <BaseDataTableV2
              className="max-h-[calc(100vh-320px)] overflow-auto"
              columns={columns}
              data={rows}
              checkboxSelection
              isLoading={isPending}
              hideFooter={false}
              pagination
              paginationMode="server"
              rowCount={rowCountState}
              pageSizeOptions={[10, 20, 50, 100]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
            />
      </BaseCard>

      {isSubQualificationModalOpen && (
        <AddEditSubQualificationModal
          open={isSubQualificationModalOpen}
          mode={subQualificationMode}
          initialData={SubQualificationInitial}
          close={() => setSubQualificationModalOpen(false)}
          refetchSubQualifications={refetch}
        />
      )}
    </>
  );
}
